import React from 'react';
import { X, Plus, Minus, ShoppingCart, Trash2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';

const CartDrawer = () => {
  const { cartItems, cartOpen, setCartOpen, removeFromCart, updateQty, totalPrice, clearCart } = useCart();

  return (
    <>
      {/* Backdrop */}
      {cartOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm"
          onClick={() => setCartOpen(false)}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full z-[90] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${cartOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        style={{ width: 'min(420px, 100vw)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ShoppingCart size={20} className="text-[#0a0a0a]" />
            <h2
              className="text-[#0a0a0a] font-black italic text-xl"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              coș ({cartItems.length})
            </h2>
          </div>
          <button
            onClick={() => setCartOpen(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <X size={22} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <ShoppingCart size={48} className="text-gray-200" />
              <p
                className="text-gray-400 font-bold text-lg"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                Coșul este gol
              </p>
              <p className="text-gray-400 text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Adaugă produse pentru a continua
              </p>
              <Link
                to="/shop"
                onClick={() => setCartOpen(false)}
                className="bg-[#0a0a0a] text-white font-black italic text-sm px-8 py-3 rounded-2xl hover:bg-[#222222] transition-colors"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                cumpără acum
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {cartItems.map((item) => (
                <div key={item.id} className="flex gap-4 bg-gray-50 rounded-2xl p-4">
                  {/* Product image */}
                  <div
                    className="flex-shrink-0 rounded-xl overflow-hidden flex items-center justify-center"
                    style={{
                      width: '80px',
                      height: '80px',
                      background: 'linear-gradient(160deg, #1a1a1a 0%, #0a0a0a 100%)',
                    }}
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4
                      className="text-[#0a0a0a] font-black italic text-sm mb-1 truncate"
                      style={{ fontFamily: 'Nunito, sans-serif' }}
                    >
                      {item.name}
                    </h4>
                    <p className="text-gray-500 text-xs mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      {item.specs}
                    </p>
                    <p className="text-[#0a0a0a] font-black text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>

                  {/* Qty controls */}
                  <div className="flex flex-col items-end gap-2">
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                    <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 px-2 py-1">
                      <button
                        onClick={() => updateQty(item.id, item.quantity - 1)}
                        className="text-[#0a0a0a] hover:opacity-70 transition-opacity"
                      >
                        <Minus size={14} />
                      </button>
                      <span
                        className="text-[#0a0a0a] font-bold text-sm w-5 text-center"
                        style={{ fontFamily: 'Nunito, sans-serif' }}
                      >
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQty(item.id, item.quantity + 1)}
                        className="text-[#0a0a0a] hover:opacity-70 transition-opacity"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-100 p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Total
              </span>
              <span
                className="text-[#0a0a0a] font-black text-xl"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                ${totalPrice.toFixed(2)}
              </span>
            </div>
            <Link
              to="/checkout"
              onClick={() => setCartOpen(false)}
              className="w-full bg-[#0a0a0a] text-white font-black italic text-base py-4 rounded-2xl text-center hover:bg-[#222222] transition-all duration-200 hover:scale-105 shadow-lg"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              finalizare comandă →
            </Link>
            <button
              onClick={clearCart}
              className="w-full text-gray-400 font-bold text-sm hover:text-red-500 transition-colors"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              golește coșul
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
